import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Profile from "./profile/profile";
import Message from "./messagesAdmin/messages";
import Notifications from "./notification/notification";
import { useNavigate } from "react-router-dom";
import { PopupContext } from "../../PopupContext";
import { ArrowLeft } from "lucide-react";

/* ============================================================
   FLÈCHE RETOUR DES POPUPS — tout est ici, dans le JS (aucun CSS)
   - toujours en haut À GAUCHE de la fenêtre du popup
   - BACK_ARROW_SIZE : taille de la flèche
   - offset (prop) : distance en px par rapport au coin haut-gauche
   - boxSelector (prop) : classe de la vraie fenêtre si elle n'est pas détectée
   - les flèches propres aux composants (packCloseIcon / lucide-arrow-left)
     sont masquées ; un clic sur la nouvelle flèche déclenche la dernière
     ancienne flèche (navigation interne inchangée).
   ============================================================ */
const BACK_ARROW_SIZE = 18;
const OWN_ARROWS = "svg.packCloseIcon, svg.lucide-arrow-left";

const findVisibleBox = (root, selector) => {
  // 1) si on connaît la classe de la vraie fenêtre, on la prend directement
  if (selector) {
    const target = root.querySelector(selector);
    if (target) {
      const r = target.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return r;
    }
  }

  // 2) sinon : premier élément visible, en ignorant les overlays plein écran
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const queue = [root];
  while (queue.length) {
    const node = queue.shift();
    const r = node.getBoundingClientRect();
    const isFullScreen = r.width >= vw * 0.98 && r.height >= vh * 0.98;
    if (r.width > 120 && r.height > 120 && !isFullScreen) return r;
    queue.push(...node.children);
  }
  return root.getBoundingClientRect();
};

const PopupBackArrow = ({
  anchorRef,
  onClick,
  color = "#fff",
  boxSelector,
  offset = 10,
}) => {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    let raf;
    let stopped = false;
    const update = () => {
      const el = anchorRef.current;
      if (el) {
        const r = findVisibleBox(el, boxSelector);
        const top = Math.round(r.top);
        const left = Math.round(r.left);
        setPos((p) =>
          p && p.top === top && p.left === left ? p : { top, left },
        );
        // masque les flèches propres au composant (sans changer la mise en page)
        el.querySelectorAll(OWN_ARROWS).forEach((n) => {
          n.style.setProperty("visibility", "hidden", "important");
          n.style.setProperty("pointer-events", "none", "important");
        });
      }
      if (!stopped) raf = requestAnimationFrame(update);
    };
    update();
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
    };
  }, [anchorRef, boxSelector]);

  const handleClick = () => {
    // on prend la DERNIÈRE flèche = la plus "profonde" (détails → liste, etc.)
    const arrows = anchorRef.current?.querySelectorAll(OWN_ARROWS);
    const inner = arrows && arrows.length ? arrows[arrows.length - 1] : null;
    if (inner) {
      inner.dispatchEvent(
        new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window,
        }),
      );
    } else {
      onClick();
    }
  };

  if (!pos) return null;

  return createPortal(
    <button
      type="button"
      aria-label="Retour"
      onClick={handleClick}
      style={{
        position: "fixed",
        top: pos.top + offset,
        left: pos.left + offset,
        zIndex: 10000,
        display: "grid",
        placeItems: "center",
        width: 32,
        height: 32,
        padding: 0,
        border: 0,
        borderRadius: 10,
        background: "transparent",
        color,
        cursor: "pointer",
      }}
    >
      <ArrowLeft size={BACK_ARROW_SIZE} color={color} />
    </button>,
    document.body,
  );
};

const SidebarComercial = () => {
  const navigate = useNavigate();
  const [isProfilePopupVisible, setProfilePopupVisible] = useState(false);
  const [isMessagesPopupVisible, setMessagesPopupVisible] = useState(false);
  const [isNotificationPopupVisible, setNotificationPopupVisible] =
    useState(false);
  const [isSettingsPopupVisible, setSettingsPopupVisible] = useState(false);

  // COMPTEUR TOTAL (identique à Sidebar admin)
  const [unreadCount, setUnreadCount] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL;
  const { openPackParent, popupRef, closeAllPopups } =
    useContext(PopupContext);

  // Références locales vers la fenêtre de chaque popup (pour placer la flèche)
  const profileBoxRef = useRef(null);
  const messagesBoxRef = useRef(null);
  const notifBoxRef = useRef(null);

  const setProfileBox = useCallback(
    (el) => {
      profileBoxRef.current = el;
      if (popupRef) popupRef.current = el;
    },
    [popupRef],
  );

  const setMessagesBox = useCallback(
    (el) => {
      messagesBoxRef.current = el;
      if (popupRef) popupRef.current = el;
    },
    [popupRef],
  );

  const getToken = () => localStorage.getItem("token");

  // MÊME LOGIQUE QUE SIDEBAR ADMIN (même API, même calcul)
  const fetchUnreadByType = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await axios.get(`${API_URL}/unread-count-Comercial-by-type`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Ton API renvoie : { POINT_RECHARGE: 7, INVOICE: 4, USER_CREATED: 2, ... }
      const counts = res.data || {};
      const total = Object.values(counts).reduce(
        (sum, count) => sum + (count || 0),
        0,
      );
      setUnreadCount(total);
    } catch (err) {
      console.error("Erreur unread-count-Comercial-by-type:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  useEffect(() => {
    fetchUnreadByType();
    const interval = setInterval(fetchUnreadByType, 20000); // même intervalle que l'admin
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, navigate]);

  // OUVRIR POPUP → reset compteur (comme admin)
  const toggleNotificationPopup = () => {
    closeAllPopups();
    if (!isNotificationPopupVisible) {
      setUnreadCount(0); // même comportement que l'admin
    }
    setNotificationPopupVisible((prev) => !prev);
  };

  const toggleProfilePopup = () => {
    closeAllPopups();
    setProfilePopupVisible((prev) => !prev);
  };
  const toggleMessagesPopup = () => {
    closeAllPopups();
    setMessagesPopupVisible((prev) => !prev);
  };
  const toggleSettingsPopup = () => {
    closeAllPopups();
    setSettingsPopupVisible((prev) => !prev);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <>
      <div className="sidebarclient">
        <div className="sidebar-overlayclient"></div>

        <div className="logos-container-navbarclient">
          {/* PROFILE */}
          <div className="logo-itemclient" onClick={toggleProfilePopup}>
            <img
              src="/images/sidebar/profile.PNG"
              alt="Profile"
              className="logo-profileclient"
            />
          </div>

          {/* MESSAGES */}
          <div className="logo-itemclient" onClick={toggleMessagesPopup}>
            <img
              src="/images/sidebar/message.PNG"
              alt="Messages"
              className="logoclient"
            />
          </div>

          {/* NOTIFICATIONS – 100% IDENTIQUE À L'ADMIN (mêmes classes) */}
          <div className="logo-itemclient" onClick={toggleNotificationPopup}>
            <div className="notification-wrapper">
              <img
                src="/images/sidebar/notifications.PNG"
                alt="Notifications"
                className="logoclient"
              />

              {unreadCount > 0 && (
                <>
                  <span className="notification-badge">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                  <div className="notification-red-dot"></div>
                </>
              )}
            </div>
          </div>

          {/* FAVORIS */}
          <div className="logo-itemclient">
            <img
              src="/images/sidebar/favorite.PNG"
              alt="Favorites"
              className="logoclient"
            />
          </div>

          {/* SETTINGS */}
          <div className="logo-itemclient" onClick={toggleSettingsPopup}>
            <img
              src="/images/sidebar/settings.PNG"
              alt="Settings"
              className="logoclient"
            />
          </div>

          {/* LOGOUT */}
          <div className="logo-itemclient" onClick={handleLogout}>
            <img
              src="/images/sidebar/logout.PNG"
              alt="Logout"
              className="logoclient"
            />
          </div>
        </div>
      </div>

      {/* ✅ Popups rendus dans document.body (hors de la sidebar)
          → même structure que la sidebar client : plus affectés par le
          backdrop-filter / overflow de .sidebarclient, centrés sur la fenêtre */}
      {createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            overflow: "visible",
            zIndex: 1000,
          }}
        >
          {/* POPUP PROFIL (fond sombre → flèche blanche) */}
          {isProfilePopupVisible && (
            <div className="sidebar-with-popup">
              <div className="overlayUB" onClick={() => openPackParent(null)}>
                <div
                  ref={setProfileBox}
                  className="PopupUnlimitedBonus-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Profile onClose={toggleProfilePopup} />
                </div>
              </div>
              <PopupBackArrow
                anchorRef={profileBoxRef}
                onClick={toggleProfilePopup}
                color="#fff"
                offset={10}
              />
            </div>
          )}

          {/* POPUP MESSAGES (fond blanc → flèche foncée) */}
          {isMessagesPopupVisible && (
            <div className="sidebar-with-popup">
              <div className="overlayUB" onClick={() => openPackParent(null)}>
                <div
                  ref={setMessagesBox}
                  className="PopupUnlimitedBonus-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Message onClose={toggleMessagesPopup} />
                </div>
              </div>
              <PopupBackArrow
                anchorRef={messagesBoxRef}
                onClick={toggleMessagesPopup}
                color="#1f2937"
                offset={24}
              />
            </div>
          )}

          {/* POPUP NOTIFICATIONS (fond sombre → flèche blanche) */}
          {isNotificationPopupVisible && (
            <div className="sidebar-with-popup">
              <div className="overlayUB" onClick={() => openPackParent(null)}>
                <div
                  ref={notifBoxRef}
                  className="PopupUnlimitedBonus-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Notifications onClose={toggleNotificationPopup} />
                </div>
              </div>
              <PopupBackArrow
                anchorRef={notifBoxRef}
                onClick={toggleNotificationPopup}
                color="#fff"
                boxSelector=".admin-notif-popup"
                offset={10}
              />
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default SidebarComercial;